import React, { useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useData } from '../../data/DataProvider';
import { pbPortal } from '../../lib/pb';
import { signOutClient } from '../../lib/auth';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../icons';

export default function PortalShell() {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useData();
  const record = pbPortal.authStore.record;
  const client = data.clients.find((item) => item.id === record?.client_id)
    || data.clients[0]
    || { initials: '??', name: 'Client' };
  const lastTrackRef = useRef(null);

  // Portal access audit trail (pass 7): record one row per page view,
  // throttled per path so rapid tab-switching doesn't flood the log. The
  // pb_hook clamps tenant_id/client_id/visited_at server-side; clients
  // can only create, never read or delete — the agency sees who visited
  // and what they opened. NB: PB 0.39 evaluates create rules against the
  // RAW submitted data BEFORE before-create hooks run, so the client scope
  // must be included in the payload itself (found 2026-08-25 — without it
  // every visit silently 400'd and the trail was empty).
  useEffect(() => {
    if (!record?.client_id) return;
    // Normalize away the tenant slug segment (the magic-link token is
    // already stripped by PortalAuthGate's redirect).
    const path = location.pathname.replace(/^\/portal\/[^/]+/, '') || '/';
    const now = Date.now();
    const last = lastTrackRef.current;
    if (last && last.path === path && now - last.at < 10_000) return;
    lastTrackRef.current = { path, at: now };
    pbPortal.collection('portal_visits').create({
      path,
      client_id: record.client_id,
      tenant_id: record.tenant_id || '',
    }).catch(() => {});
  }, [location, record]);

  const base = tenantSlug ? `/portal/${tenantSlug}` : '/portal';
  const links = [
    { path: '', label: 'Home', icon: Icons.Home, end: true },
    { path: 'request-job', label: 'Request a Job', icon: Icons.Plus },
    { path: 'invoices', label: 'Invoices', icon: Icons.FileText },
    { path: 'estimates', label: 'Estimates', icon: Icons.FileText },
    { path: 'quotes', label: 'Quotes', icon: Icons.FileText },
    { path: 'history', label: 'History', icon: Icons.Tool },
    { path: 'keydates', label: 'Dates', icon: Icons.Calendar },
    { path: 'documents', label: 'Files', icon: Icons.FileText },
    { path: 'chat', label: 'Messages', icon: Icons.MessageCircle },
  ];

  const signOut = () => {
    signOutClient();
    navigate(`/portal/login${tenantSlug ? `/${tenantSlug}` : ''}`, { replace: true });
  };

  return (
    <div className="portal-frame">
      <header className="portal-header" style={{ '--portal-brand': data.tenant.primary_color }}>
        <div className="brand">
          <div className="brand-mark"><SafeIcon icon={Icons.Droplet} /></div>
          <div>
            <strong>{(data.tenant.name || 'Client Portal').split(' ')[0].toLowerCase()}</strong>
            <span>PLUMBING &amp; HEATING</span>
          </div>
        </div>
        <nav className="portal-nav">
          {links.map((link) => (
            <NavLink key={link.label} end={link.end} to={`${base}/${link.path}`}>{link.label}</NavLink>
          ))}
        </nav>
        <div className="portal-account">
          <span>{client.initials}</span>
          <strong>{client.name}</strong>
          <button
            className="icon-button portal-logout"
            aria-label="Sign out"
            title="Sign out"
            onClick={signOut}
          >
            <SafeIcon icon={Icons.LogOut} />
          </button>
        </div>
      </header>
      <main className="portal-main"><Outlet /></main>
      <nav className="mobile-tabs">
        {links.map((link) => (
          <NavLink key={link.label} end={link.end} to={`${base}/${link.path}`}>
            <SafeIcon icon={link.icon} /><span>{link.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
